"use client";

import { useEffect, useState } from "react";
import {
  Wallet,
  Shield,
  TrendingUp,
  ArrowUpRight,
  Coins,
  AlertCircle,
  Megaphone,
  CalendarClock,
  Server,
  Database,
  RefreshCw,
  ClipboardCheck,
  ListChecks,
  ShieldCheck,
} from "lucide-react";
import Link from "next/link";
import { useAuthStore } from "@/store/auth-store";
import { cn } from "@/lib/utils";

/* ── 타입 ── */
interface StatCard {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  href: string;
  description?: string;
}

interface TokenActivity {
  id: string;
  contractId: string;
  name: string;
  symbol: string;
  status: string;
  amount: number;
  totalSupply: number;
  updatedAt: string;
}

interface PublicOffering {
  id: string;
  contractId: string;
  name: string;
  symbol: string;
  publicOfferingAmount: number;
  publicOfferingPrice: number;
  publicOfferingStartDate: string;
  publicOfferingEndDate: string;
  numberOfTokensRaised: number;
  securityTokenStatus: string;
}

interface SystemHealth {
  bridge: "online" | "offline" | "checking";
  chain: "online" | "offline" | "checking";
}

/* ── 상태 배지 ── */
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; text: string; dot: string }> = {
    생성완료: { bg: "bg-blue-50 dark:bg-blue-950/50", text: "text-blue-700 dark:text-blue-300", dot: "bg-blue-500" },
    발행완료: { bg: "bg-emerald-50 dark:bg-emerald-950/50", text: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
    공모중: { bg: "bg-amber-50 dark:bg-amber-950/50", text: "text-amber-700 dark:text-amber-300", dot: "bg-amber-500" },
    청산완료: { bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400", dot: "bg-gray-400" },
  };
  const s = map[status] ?? { bg: "bg-gray-50 dark:bg-gray-800", text: "text-gray-700 dark:text-gray-300", dot: "bg-gray-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {status || "알 수 없음"}
    </span>
  );
}

/* ── 공모 진행률 바 ── */
function ProgressBar({ raised, total }: { raised: number; total: number }) {
  const pct = total > 0 ? Math.min((raised / total) * 100, 100) : 0;
  return (
    <div className="mt-2.5">
      <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
        <span>{raised.toLocaleString()} / {total.toLocaleString()} 토큰</span>
        <span className="font-semibold text-gray-700 dark:text-gray-300">{pct.toFixed(1)}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-700",
            pct >= 100
              ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
              : pct >= 50
                ? "bg-gradient-to-r from-blue-500 to-blue-400"
                : "bg-gradient-to-r from-amber-500 to-amber-400"
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ── 공모 D-Day ── */
function DDay({ endDate, now }: { endDate: string; now: number }) {
  if (!endDate) return null;
  const diff = Math.ceil((new Date(endDate).getTime() - now) / 86400000);
  if (diff < 0) return <span className="text-[11px] text-gray-400">마감</span>;
  if (diff === 0) return <span className="text-[11px] font-bold text-red-500">D-Day</span>;
  return <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400">D-{diff}</span>;
}

/* ── 시스템 상태 패널 ── chain-first 환경: bridge + chain 2개 컴포넌트 */
function SystemStatusPanel({ health }: { health: SystemHealth }) {
  const items = [
    { key: "bridge" as const, label: "Bridge API", icon: Server },
    { key: "chain"  as const, label: "Hyperledger Fabric", icon: Database },
  ];

  const statusColors = {
    online: "text-emerald-600 dark:text-emerald-400",
    offline: "text-red-500",
    checking: "text-amber-500 animate-pulse",
  };
  const statusDots = {
    online: "bg-emerald-500",
    offline: "bg-red-500",
    checking: "bg-amber-500 animate-pulse",
  };
  const statusLabels: Record<SystemHealth["bridge"], string> = {
    online: "정상",
    offline: "오프라인",
    checking: "확인 중",
  };

  return (
    <div className="flex items-center gap-5">
      {items.map((item) => (
        <div key={item.key} className="flex items-center gap-2" title={statusLabels[health[item.key]]}>
          <item.icon className={cn("h-3.5 w-3.5", statusColors[health[item.key]])} />
          <span className="text-xs text-gray-500 dark:text-gray-400">{item.label}</span>
          <span className={cn("h-1.5 w-1.5 rounded-full", statusDots[health[item.key]])} />
        </div>
      ))}
    </div>
  );
}

/* ── 스켈레톤 로더 ── */
function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="skeleton h-8 w-48" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-950">
            <div className="skeleton mb-3 h-4 w-20" />
            <div className="skeleton h-8 w-16" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <div className="skeleton mb-4 h-5 w-32" />
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} className="mb-3 flex gap-3">
                <div className="skeleton h-10 w-10 rounded-lg" />
                <div className="flex-1">
                  <div className="skeleton mb-1 h-4 w-3/4" />
                  <div className="skeleton h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 메인 ── */
export default function DashboardPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userCount, setUserCount] = useState(0);
  const [tokenCount, setTokenCount] = useState(0);
  const [walletCount, setWalletCount] = useState(0);
  const [totalSupply, setTotalSupply] = useState(0);
  const [tokens, setTokens] = useState<TokenActivity[]>([]);
  const [offerings, setOfferings] = useState<PublicOffering[]>([]);
  const [pendingSubs, setPendingSubs] = useState(0);          // 청약 처리 대기
  const [pendingBR, setPendingBR] = useState(0);               // 권리자명부 KSD 미제출
  const [complianceScore, setComplianceScore] = useState<number | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(() => new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [health, setHealth] = useState<SystemHealth>({
    bridge: "checking",
    chain: "checking",
  });
  // mount 1회 캡처 (인사말·D-Day 등 — react-hooks/purity)
  const [mountedHour] = useState<number>(() => new Date().getHours());
  const [mountedNow]  = useState<number>(() => Date.now());

  const loadData = async () => {
    setError(null);
    try {
      // 모두 chain 1차 소스 (admin-next 의 자체 API 라우트, 내부에서 bridge 호출)
      const [walletsRes, tokensRes, offeringsRes, subsRes, brRes, complianceRes] = await Promise.allSettled([
        fetch("/api/wallets", { cache: "no-store" }),
        fetch("/api/account-manager/issuer-accounts", { cache: "no-store" }),
        fetch("/api/public-offerings", { cache: "no-store" }),
        fetch("/api/account-manager/subscriptions", { cache: "no-store" }),
        fetch("/api/account-manager/beneficiary-register", { cache: "no-store" }),
        fetch("/api/account-manager/compliance", { cache: "no-store" }),
      ]);

      // wallets → 사용자/지갑 카운트 (체인 wallet 한 개 = 사용자 한 명)
      if (walletsRes.status === "fulfilled" && walletsRes.value.ok) {
        const d = await walletsRes.value.json();
        const items = (d?.items ?? []) as Array<{ role: string }>;
        setWalletCount(items.length);
        setUserCount(items.length); // wallet = 사용자 1:1 매핑
      }

      // tokens → 발행인관리계좌부 (체인의 모든 발행 토큰)
      if (tokensRes.status === "fulfilled" && tokensRes.value.ok) {
        const d = await tokensRes.value.json();
        const list = (d?.items ?? []) as Array<{
          contractId: string; tokenName: string; tokenSymbol: string;
          status: string; outstanding: number; totalIssued: number;
        }>;
        setTokenCount(list.length);
        setTotalSupply(list.reduce((s, t) => s + (t.totalIssued || 0), 0));

        const mapped: TokenActivity[] = list.map((t) => ({
          id: t.contractId,
          contractId: t.contractId,
          name: t.tokenName,
          symbol: t.tokenSymbol,
          status: t.status,
          amount: t.outstanding,
          totalSupply: t.totalIssued,
          updatedAt: "",                                 // mount 시점 ISO 대신 생략
        }));
        setTokens(mapped.slice(0, 5));
      }

      // offerings → 공모 정보 (체인 일정)
      if (offeringsRes.status === "fulfilled" && offeringsRes.value.ok) {
        const d = await offeringsRes.value.json();
        const list = (d?.items ?? []) as Array<{
          contractId: string; symbol: string; name: string;
          publicOfferingAmount: number; publicOfferingPrice: number;
          publicOfferingStartDate: string; publicOfferingEndDate: string;
          phase: string;
        }>;
        const offeringList: PublicOffering[] = list.map((o) => ({
          id: o.contractId,
          contractId: o.contractId,
          name: o.name,
          symbol: o.symbol,
          publicOfferingAmount: o.publicOfferingAmount,
          publicOfferingPrice: o.publicOfferingPrice,
          publicOfferingStartDate: o.publicOfferingStartDate,
          publicOfferingEndDate: o.publicOfferingEndDate,
          numberOfTokensRaised: 0,
          securityTokenStatus: o.phase === "공모중" ? "공모중" : o.phase === "거래중" ? "발행완료" : "생성완료",
        }));
        setOfferings(offeringList);
      }

      // 청약 처리 대기 (시행령 한도·적합성 점검 대기)
      if (subsRes.status === "fulfilled" && subsRes.value.ok) {
        const d = await subsRes.value.json();
        const items = (d?.items ?? []) as Array<{ status: string }>;
        setPendingSubs(items.filter((s) => s.status === "신청" || s.status === "심사중" || s.status === "승인").length);
      }

      // 권리자명부 KSD 미제출 (전자증권법 §39)
      if (brRes.status === "fulfilled" && brRes.value.ok) {
        const d = await brRes.value.json();
        const items = (d?.items ?? []) as Array<{ reportStatus: string }>;
        setPendingBR(items.filter((b) => b.reportStatus === "준비중" || b.reportStatus === "검토중" || b.reportStatus === "재제출").length);
      }

      // 운영 컴플라이언스 종합 점수
      if (complianceRes.status === "fulfilled" && complianceRes.value.ok) {
        const d = await complianceRes.value.json();
        if (typeof d?.overallScore === "number") setComplianceScore(d.overallScore);
      }
    } catch (e: unknown) {
      setError((e as Error)?.message ?? "데이터를 불러올 수 없습니다");
    }
    setLastRefresh(new Date());
  };

  const checkHealth = async () => {
    // chain-first 환경: admin-next API → bridge → Hyperledger Fabric
    const tokensOk = await fetch("/api/account-manager/issuer-accounts?_t=" + Date.now(), {
      cache: "no-store", signal: AbortSignal.timeout(5000),
    }).then((r) => r.ok).catch(() => false);
    setHealth({
      bridge: tokensOk ? "online" : "offline",
      chain: tokensOk ? "online" : "offline",
    });
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadData();
      await checkHealth();
      setLoading(false);
    };
    init();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await checkHealth();
    setRefreshing(false);
  };

  const stats: StatCard[] = [
    { label: "발행 토큰", value: tokenCount, icon: Shield, color: "text-emerald-600", bgColor: "bg-emerald-50 dark:bg-emerald-950/50", href: "/security-tokens", description: "발행된 STO" },
    { label: "활성 지갑·사용자", value: walletCount, icon: Wallet, color: "text-violet-600", bgColor: "bg-violet-50 dark:bg-violet-950/50", href: "/wallets", description: "체인 wallet · 사용자" },
    { label: "총 발행량", value: totalSupply.toLocaleString(), icon: TrendingUp, color: "text-amber-600", bgColor: "bg-amber-50 dark:bg-amber-950/50", href: "/security-tokens", description: "전체 토큰 수량" },
    { label: "처리 대기 청약", value: pendingSubs, icon: ClipboardCheck, color: "text-blue-600", bgColor: "bg-blue-50 dark:bg-blue-950/50", href: "/account-manager/subscriptions", description: "신청·심사·승인 단계" },
    { label: "권리자명부 미제출", value: pendingBR, icon: ListChecks, color: "text-purple-600", bgColor: "bg-purple-50 dark:bg-purple-950/50", href: "/account-manager/beneficiary-register", description: "KSD 제출 대기" },
    { label: "컴플라이언스 점수", value: complianceScore !== null ? `${complianceScore}/100` : "—",
      icon: ShieldCheck,
      color: complianceScore !== null && complianceScore >= 90 ? "text-emerald-600" : complianceScore !== null && complianceScore >= 70 ? "text-amber-600" : "text-rose-600",
      bgColor: complianceScore !== null && complianceScore >= 90 ? "bg-emerald-50 dark:bg-emerald-950/50" : complianceScore !== null && complianceScore >= 70 ? "bg-amber-50 dark:bg-amber-950/50" : "bg-rose-50 dark:bg-rose-950/50",
      href: "/account-manager/compliance",
      description: "자본·인증·BCP 종합" },
  ];

  if (loading) return <DashboardSkeleton />;

  // 시간 인사 — mount 시점 hour 캡처
  const greeting = mountedHour < 12 ? "좋은 아침이에요" : mountedHour < 18 ? "좋은 오후에요" : "좋은 저녁이에요";
  // userCount 는 walletCount 와 1:1 매핑이지만 KPI 카드에서 walletCount 를 표시하므로 직접 사용 안 함
  void userCount;

  return (
    <div className="space-y-6">
      {/* 헤더 영역 */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {greeting}, {user?.name || "관리자"}님
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            WonLET 토큰증권 관리 현황을 확인하세요.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SystemStatusPanel health={health} />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg border border-gray-200 p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-gray-800"
            title="새로고침"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* 에러 배너 */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ── 통계 카드 ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {stats.map((s) => (
          <Link
            key={s.label}
            href={s.href}
            className="group rounded-xl border border-gray-200/80 bg-white p-5 transition-all duration-200 hover:border-gray-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-950 dark:hover:border-gray-700"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{s.label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">{s.value}</p>
                <p className="mt-1 text-[11px] text-gray-400">{s.description}</p>
              </div>
              <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl", s.bgColor)}>
                <s.icon className={cn("h-5 w-5", s.color)} />
              </div>
            </div>
            <div className="mt-3 flex items-center text-[11px] font-medium text-blue-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-blue-400">
              자세히 보기 <ArrowUpRight className="ml-0.5 h-3 w-3" />
            </div>
          </Link>
        ))}
      </div>

      {/* ── 하단 두 컬럼 ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 최근 토큰 활동 */}
        <div className="rounded-xl border border-gray-200/80 bg-white dark:border-gray-800 dark:bg-gray-950">
          <div className="flex items-center justify-between px-5 py-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <Coins className="h-4 w-4 text-emerald-500" />
              최근 토큰 활동
            </h2>
            <Link href="/security-tokens" className="text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">
              전체 보기 →
            </Link>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
            {tokens.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <Shield className="mx-auto mb-2 h-8 w-8 text-gray-200 dark:text-gray-700" />
                <p className="text-sm text-gray-400">발행된 토큰이 없습니다</p>
              </div>
            ) : (
              tokens.map((t) => (
                <Link
                  key={t.id}
                  href={`/security-tokens/${t.id}`}
                  className="flex items-center justify-between px-5 py-3 transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-900/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {t.name}
                      </p>
                      <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-gray-800">
                        {t.symbol}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-gray-400">
                      발행 {t.amount.toLocaleString()} · 총량 {t.totalSupply.toLocaleString()}
                    </p>
                  </div>
                  <div className="ml-4 flex flex-col items-end gap-1">
                    <StatusBadge status={t.status} />
                    {t.updatedAt && (
                      <span className="text-[10px] text-gray-400">
                        {new Date(t.updatedAt).toLocaleDateString("ko-KR")}
                      </span>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* 공모 현황 */}
        <div className="rounded-xl border border-gray-200/80 bg-white dark:border-gray-800 dark:bg-gray-950">
          <div className="flex items-center justify-between px-5 py-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <Megaphone className="h-4 w-4 text-amber-500" />
              공모 현황
            </h2>
            <Link href="/public-offerings" className="text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">
              전체 보기 →
            </Link>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
            {offerings.length === 0 ? (
              <div className="px-5 py-12 text-center">
                <Megaphone className="mx-auto mb-2 h-8 w-8 text-gray-200 dark:text-gray-700" />
                <p className="text-sm text-gray-400">진행 중인 공모가 없습니다</p>
              </div>
            ) : (
              offerings.map((o) => (
                <div key={o.id} className="px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{o.name}</p>
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-gray-800">
                        {o.symbol}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DDay endDate={o.publicOfferingEndDate} now={mountedNow} />
                      <StatusBadge status={o.securityTokenStatus} />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-[11px] text-gray-500">
                    <span className="flex items-center gap-1">
                      <CalendarClock className="h-3 w-3" />
                      {o.publicOfferingStartDate
                        ? `${new Date(o.publicOfferingStartDate).toLocaleDateString("ko-KR")} ~ ${new Date(o.publicOfferingEndDate).toLocaleDateString("ko-KR")}`
                        : "일정 미정"}
                    </span>
                    <span>공모가 {o.publicOfferingPrice.toLocaleString()}원</span>
                  </div>
                  <ProgressBar raised={o.numberOfTokensRaised} total={o.publicOfferingAmount} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 마지막 갱신 시간 */}
      <div className="text-center">
        <span className="text-[11px] text-gray-400">
          마지막 갱신: {lastRefresh.toLocaleString("ko-KR")}
        </span>
      </div>
    </div>
  );
}
